<script setup lang="ts">
import type { InvoiceIndexEntry } from '@/packages/api/src';
import TableRow from '@/Components/TableRow.vue';
import { router } from '@inertiajs/vue3';
import InvoiceMoreOptionsDropdown from './InvoiceMoreOptionsDropdown.vue';
import InvoiceDownloadDropdown from './InvoiceDownloadDropdown.vue';
import InvoiceDuplicateModal from './InvoiceDuplicateModal.vue';
import {
    ArrowDownTrayIcon,
    PencilSquareIcon,
    DocumentDuplicateIcon,
    CheckCircleIcon,
    XMarkIcon,
    TrashIcon,
} from '@heroicons/vue/20/solid';
import LoadingSpinner from '@/packages/ui/src/LoadingSpinner.vue';
import { formatDate } from '@/packages/ui/src/utils/time';
import { Button } from '@/packages/ui/src/Buttons';
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuTrigger,
} from '@/packages/ui/src';
import { computed, inject, ref, type ComputedRef } from 'vue';
import type { Organization } from '@/packages/api/src';
import { useInvoiceActions } from '../../../utils/useInvoiceActions';

const props = defineProps<{
    invoice: InvoiceIndexEntry;
}>();

const { deleteInvoice, setPaid, setUnpaid } = useInvoiceActions(() => props.invoice.id);

const isPaid = computed(() => props.invoice.paid_date !== null);
const showDuplicateModal = ref(false);

function handleEdit() {
    router.visit(`/invoices/${props.invoice.id}`);
}

function handleDuplicate() {
    showDuplicateModal.value = true;
}

const organization = inject<ComputedRef<Organization>>('organization');
</script>

<template>
    <ContextMenu>
        <ContextMenuTrigger as-child>
            <TableRow :href="`/invoices/${invoice.id}`">
                <div
                    class="whitespace-nowrap flex items-center space-x-5 3xl:pl-12 py-4 pr-3 text-sm font-medium text-text-primary pl-4 sm:pl-6 lg:pl-8 3xl:pl-12">
                    <span>
                        {{ invoice.reference }}
                    </span>
                </div>
                <div class="px-3 py-4 text-sm text-text-primary">
                    {{ invoice.buyer_name }}
                </div>
                <div class="px-3 py-4 text-sm text-text-primary">
                    {{ invoice.date ? formatDate(invoice.date, organization?.date_format) : '--' }}
                </div>
                <div class="px-3 py-4 text-sm text-text-primary">
                    {{
                        invoice.due_at
                            ? formatDate(invoice.due_at, organization?.date_format)
                            : '--'
                    }}
                </div>
                <div class="px-3 py-4 text-sm text-text-primary">
                    {{
                        invoice.paid_date
                            ? formatDate(invoice.paid_date, organization?.date_format)
                            : '--'
                    }}
                </div>
                <div class="px-3 py-2 text-sm flex items-center text-text-primary">
                    <InvoiceDownloadDropdown :invoice="invoice">
                        <template #default="{ loading }">
                            <Button
                                variant="ghost"
                                class="text-text-tertiary hover:text-text-primary">
                                <LoadingSpinner v-if="loading" class="mx-0" />
                                <ArrowDownTrayIcon v-else class="h-5 w-5" />
                            </Button>
                        </template>
                    </InvoiceDownloadDropdown>
                </div>
                <div
                    class="relative whitespace-nowrap flex items-center space-x-4 pl-3 text-right text-sm font-medium sm:pr-0 pr-4 sm:pr-6 lg:pr-8 3xl:pr-12">
                    <InvoiceMoreOptionsDropdown
                        :invoice="invoice"
                        @edit="handleEdit"
                        @duplicate="handleDuplicate"
                        @delete="deleteInvoice"
                        @set-paid="setPaid"
                        @set-unpaid="setUnpaid" />
                </div>
            </TableRow>
        </ContextMenuTrigger>
        <ContextMenuContent class="min-w-[160px]">
            <ContextMenuItem class="space-x-3" @select="handleEdit">
                <PencilSquareIcon class="w-4 h-4 text-icon-default" />
                <span>Edit</span>
            </ContextMenuItem>
            <ContextMenuItem class="space-x-3" @select="handleDuplicate">
                <DocumentDuplicateIcon class="w-4 h-4 text-icon-default" />
                <span>Duplicate</span>
            </ContextMenuItem>
            <ContextMenuItem v-if="!isPaid" class="space-x-3" @select="setPaid">
                <CheckCircleIcon class="w-4 h-4 text-icon-default" />
                <span>Mark as Paid</span>
            </ContextMenuItem>
            <ContextMenuItem v-else class="space-x-3" @select="setUnpaid">
                <XMarkIcon class="w-4 h-4 text-icon-default" />
                <span>Mark as Unpaid</span>
            </ContextMenuItem>
            <ContextMenuSeparator />
            <ContextMenuItem class="space-x-3 text-destructive" @select="deleteInvoice">
                <TrashIcon class="w-4 h-4 text-icon-default" />
                <span>Delete</span>
            </ContextMenuItem>
        </ContextMenuContent>
    </ContextMenu>

    <InvoiceDuplicateModal v-model:show="showDuplicateModal" :invoice-id="invoice.id" />
</template>

<style scoped></style>
